import { Link } from '@tanstack/react-router'
import { MoreHorizontalIcon } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from './sheet'

export type MobileDockItem = {
  id: string
  icon: ReactNode
  label: string
  /** Where the tile goes — same contract as `DockItem`. */
  to?: string
}

type MobileDockProps = {
  items: MobileDockItem[]
  /**
   * Sections that do not fit the bar, reached through one more tile.
   *
   * They used to be dropped outright and left «reachable by direct URL», which
   * on a phone means not reachable: an admin signed in there could see four of
   * their seven sections and had no way to the rest. The bar cannot hold seven
   * labelled tiles — at 320px it cannot hold seven unlabelled ones — so what
   * does not fit moves behind a sheet rather than off the device.
   */
  overflowItems?: MobileDockItem[]
  activeId?: string
  onSelect?: (id: string) => void
  className?: string
}

/*
 * Icons only, like the desktop rail. The label used to sit under each one
 * because a phone has no hover to reveal a tooltip — but «Учётная запись» at
 * 9.5px is what decided how many tiles the bar could hold, and it held four of
 * seven sections. Dropped, the tile costs its 44px target and nothing more.
 *
 * The name does not disappear with the text: it moves to `aria-label`, so the
 * button still announces itself. A visual tooltip can hang off the same string
 * later without changing anything here.
 *
 * `flex-1` splits the bar evenly, `min-w-11` keeps every target at the touch
 * floor while it does.
 */
const TILE =
  'flex min-h-11 min-w-11 flex-1 items-center justify-center rounded-lg px-2 py-1 press [&_svg]:size-icon-lg'

const toneFor = (isActive: boolean) =>
  cn(TILE, isActive ? 'bg-primary/16 text-accent-text' : 'text-fg-secondary')

/**
 * What the rail becomes below 640: a bottom bar of icon tiles. Targets stay at
 * 44×44 — the floor for touch — and the names live in `aria-label`, where a
 * tooltip can pick them up later.
 *
 * What does not fit goes behind one more tile, into a sheet. Six tiles is the
 * ceiling: 44 apiece is 264, and the bar has 268 to spend at 320px.
 */
export function MobileDock({
  items,
  overflowItems = [],
  activeId,
  onSelect,
  className,
}: MobileDockProps) {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)

  // The overflow tile answers for whatever is inside it: on a section that only
  // exists in the sheet, nothing in the bar would light up otherwise, and the
  // user would read the bar as «I am nowhere».
  const isOverflowActive = overflowItems.some((item) => item.id === activeId)

  const tile = (item: MobileDockItem, onActivate?: () => void) => {
    const isActive = item.id === activeId

    // A link when it navigates — see `DockItem.to`.
    return item.to === undefined ? (
      <button
        key={item.id}
        type="button"
        onClick={() => {
          onActivate?.()
          onSelect?.(item.id)
        }}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
        className={toneFor(isActive)}
      >
        {item.icon}
      </button>
    ) : (
      <Link
        key={item.id}
        to={item.to}
        onClick={() => {
          onActivate?.()
          onSelect?.(item.id)
        }}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
        className={toneFor(isActive)}
      >
        {item.icon}
      </Link>
    )
  }

  return (
    <>
      <nav
        aria-label={t('ui.sections')}
        data-slot="mobile-dock"
        className={cn(
          'flex items-center justify-between gap-1 rounded-2xl border border-border-strong bg-overlay px-2.5 py-2 backdrop-blur-[20px] shadow-[0_12px_30px_rgb(0_0_0/0.35)]',
          className,
        )}
      >
        {items.map((item) => tile(item))}

        {overflowItems.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setIsOpen(true)
            }}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={t('ui.more')}
            className={toneFor(isOverflowActive)}
          >
            <MoreHorizontalIcon />
          </button>
        )}
      </nav>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {/* Bottom, not right: it is opened from the bottom bar by a thumb, and
            a panel that flies in from the far edge of the screen reads as
            belonging to something else. `pb-8` clears the home indicator. */}
        <SheetContent side="bottom" className="max-h-[70vh] gap-0 rounded-t-2xl pb-8">
          <SheetHeader>
            <SheetTitle>{t('ui.sections')}</SheetTitle>
          </SheetHeader>

          <div className="flex flex-col overflow-y-auto px-2 pb-2">
            {overflowItems.map((item) =>
              // A row, not a tile: there is width to spend here, and a list
              // reads faster than a grid of things that all look alike.
              item.to === undefined ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setIsOpen(false)
                    onSelect?.(item.id)
                  }}
                  className={rowFor(item.id === activeId)}
                >
                  {item.icon}
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={() => {
                    setIsOpen(false)
                    onSelect?.(item.id)
                  }}
                  aria-current={item.id === activeId ? 'page' : undefined}
                  className={rowFor(item.id === activeId)}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ),
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

const rowFor = (isActive: boolean) =>
  cn(
    'flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-body press [&_svg]:size-icon-lg',
    isActive ? 'bg-primary/16 text-accent-text' : 'text-fg-body',
  )
